/**
 * connectProvider — THE single connect/scope-grant flow for the whole app.
 *
 * Every "Connect <provider>" / "Enable <capability>" / "add permissions" entry
 * point (onboarding, contacts, drive, calendar, email, chat chips, the
 * integrations modal, scope-upgrade prompts) MUST call this. There is no other
 * connect path — the old `triggerOAuthPopup` is gone, and no component builds its
 * own `/integrations/.../auth` request. Fixing the connect UX here fixes it
 * everywhere; that's the whole point of having one function.
 *
 * It is a same-window redirect (no popup): we fetch the provider authorize URL
 * from the backend (which owns scope resolution, never include_granted_scopes,
 * and — when `integrationId` is present — the incremental-consent flow:
 * login_hint + no forced prompt, so an EXPANSION skips the account chooser and
 * only consents the new scope) and navigate to it. The backend 302s back to
 * `redirectUrl` with integration_connected/calendar_connected, which the
 * SessionProvider + App return handlers consume.
 *
 * Pre-redirect side effects (e.g. onboarding's convert-from-demo marker) belong
 * in the caller, BEFORE calling this — the page unloads on redirect.
 */

import { apiFetch } from '@/lib/apiClient'
import { isNative } from '@/lib/capacitor'

export interface ConnectProviderOptions {
  /** Provider key, e.g. 'google' | 'microsoft' | 'shopify'. */
  provider: string
  /** Session auth token for the authorize-URL fetch. */
  authToken: string | null
  /**
   * Capability being granted (ADR 017 D4), forwarded as `?capability=`. The
   * backend resolves the explicit scope union; the frontend never names raw
   * OAuth scopes.
   */
  capability?: string
  /**
   * Existing integration being EXPANDED (incremental consent). Forwarded as
   * `?integrationId=`. Its presence is what makes the backend run the incremental
   * flow (login_hint + drop the forced prompt) and union the account's already-
   * granted scopes so nothing is dropped.
   */
  integrationId?: string
  /** Active conversation id — carried into the per-provider oauth_state row. */
  conversationId?: string
  /** Shopify store subdomain (e.g. 'mystore'). */
  shopDomain?: string
  /**
   * Where the backend should 302 back to after the grant. Defaults to the
   * current in-app URL so the user returns exactly where they were (the in-app
   * onboarding connect step relies on this default — there is no /welcome page).
   */
  redirectUrl?: string
  /**
   * Email to send as `login_hint` so the provider targets THIS account and skips
   * its account chooser — for connecting the account the user is already signed in
   * as (e.g. onboarding right after SSO), where re-picking is redundant. OMIT it
   * for "add a different account" (Manage Accounts) so the chooser still appears.
   * The backend also drops Microsoft's `select_account` when a login_hint is set.
   */
  loginHint?: string
}

/**
 * Start a connect/scope-grant: fetch the authorize URL and navigate to it.
 *
 * Resolves only if the fetch fails (the success path navigates away and never
 * resolves). On failure it throws so the caller can surface an error and restore
 * its button state.
 */
export async function connectProvider(opts: ConnectProviderOptions): Promise<never | void> {
  const params = new URLSearchParams({
    redirectUrl: opts.redirectUrl ?? window.location.href,
  })
  // Native (Capacitor iOS): mark the flow so the backend returns via the
  // server-constructed xavior://integrations/callback custom scheme instead of the
  // in-WebView redirectUrl (unreachable from the external Safari that runs the
  // consent). Web is unaffected — isNative() is only true in the native shell.
  // sprint native-oauth-pass Ch.4.
  if (isNative()) params.set('platform', 'capacitor')
  if (opts.capability) params.set('capability', opts.capability)
  if (opts.integrationId) params.set('integrationId', opts.integrationId)
  if (opts.conversationId) params.set('conversationId', opts.conversationId)
  if (opts.loginHint) params.set('login_hint', opts.loginHint)
  if (opts.provider === 'shopify' && opts.shopDomain) {
    params.set('shopDomain', opts.shopDomain)
  }

  const data = (await apiFetch(`/integrations/${opts.provider}/auth?${params.toString()}`, {
    authToken: opts.authToken ?? undefined,
  })) as { authUrl: string }

  // Native (Capacitor iOS): open the provider consent in the EXTERNAL system
  // browser (window.open '_blank' → full Safari) so the WebView — and with it
  // DeepLinkHandler's appUrlOpen listener — stays alive to catch the
  // xavior://integrations/callback return. Navigating the WebView itself
  // (window.location) would tear that listener down and drop the deep link (the
  // SSO bug fixed in Ch.3). Web: a normal same-window navigation. (Mirrors
  // LoginV2.handleSso; kept inline rather than a shared helper so each site stays
  // directly testable via the isNative() export mock.) sprint native-oauth-pass Ch.4.
  if (isNative()) {
    window.open(data.authUrl, '_blank', 'noopener')
    // Preserve the documented contract: the success path does NOT resolve. On web
    // window.location.href unloads the page (the await never settles); on native the
    // WebView stays alive while consent runs in external Safari, so we must NOT
    // resolve here either — the grant returns asynchronously via the
    // xavior://integrations/callback deep link, which full-reloads the app
    // (DeepLinkHandler), tearing down this pending promise. Resolving instead would
    // let callers that only reset their busy state in `catch` (ConnectActionStep,
    // the IntegrationsModal handlers) leave the Connect button stuck spinning on the
    // happy path. native-oauth-pass Ch.4 review fix.
    return new Promise<never>(() => {})
  }
  window.location.href = data.authUrl
}

/**
 * Connect-return `?error=` codes that belong to OTHER surfaces, not the connect
 * flow: `sso_*` is the SSO login redirect consumed by LoginV2 on /login, and
 * `fatsecret_*` is the client portal's nutrition-connect redirect. The App-level
 * connect return handler must leave their params untouched (no toast, no URL
 * cleanup) so those surfaces can render them.
 */
const FOREIGN_ERROR_PREFIXES = ['sso_', 'fatsecret_']

/**
 * Map a connect-return `?error=` code (set by the backend OAuth callback's
 * failure redirects — provider-oauth.router) to the toast message the App-level
 * return handler shows (#5233).
 *
 * - Known codes keep their specific copy (provider-aware where the backend tags
 *   `?provider=` on the redirect; neutral when absent).
 * - `partial_grant`: the user unchecked mail or calendar on the consent screen,
 *   leaving a first-connect workspace `pending_connect` (reaper-bound) — the
 *   message tells them to reconnect with both boxes checked (the connect page
 *   they land back on is the reconnect affordance).
 * - UNKNOWN codes (the callback forwards raw post-auth-hook error messages) get
 *   a generic, honest message naming the code — never the old silent
 *   early-return with params left in the URL.
 * - Codes owned by other surfaces (sso_*, fatsecret_*) return null: not ours to
 *   toast or clean.
 */
export function describeConnectReturnError(
  code: string,
  provider?: string | null,
): string | null {
  if (FOREIGN_ERROR_PREFIXES.some((prefix) => code.startsWith(prefix))) return null
  const providerName = provider
    ? provider.charAt(0).toUpperCase() + provider.slice(1)
    : ''
  const deniedSubject = providerName ? `${providerName} authorization` : 'Authorization'
  const messages: Record<string, string> = {
    oauth_denied: `${deniedSubject} was denied. Please try again and grant the required permissions.`,
    oauth_missing_email: 'Could not determine your email. Please try connecting again.',
    no_calendar: 'No connected calendar found for your account.',
    oauth_failed: 'Something went wrong connecting your account. Please try again.',
    temporarily_unavailable:
      'Service is temporarily unavailable. Please try connecting again in a moment.',
    token_exchange_failed: 'Something went wrong connecting your account. Please try again.',
    partial_grant:
      'We need both email and calendar access to set up your workspace — please connect again and keep both boxes checked.',
  }
  return messages[code] ?? `Connection failed (${code}) — please try again.`
}
